/**
 * Achievement Gallery Component
 * Displays paginated achievements with knight chess theme
 */

class AchievementGallery {
    constructor() {
        this.achievements = [];
        this.currentPage = 1;
        this.pageSize = 12;
        this.totalPages = 1;
        this.imageCache = new Map();
        this.track = document.getElementById('knightTrack');
        this.dotsContainer = document.getElementById('galleryDots');
    }

    async init() {
        if (!this.track) {
            console.error('Achievement gallery track not found');
            return;
        }

        try {
            await this.loadAchievements();
            this.render();
            this.setupNavigation();
        } catch (error) {
            console.error('Failed to initialize achievement gallery:', error);
            this.renderError();
        }
    }

    async loadAchievements(page = 1) {
        try {
            const response = await fetch(`/api/achievements?page=${page}&limit=${this.pageSize}`);
            const data = await response.json();

            if (response.ok) {
                this.achievements = data.achievements || [];
                this.currentPage = data.pagination?.currentPage || page;
                this.totalPages = data.pagination?.totalPages || 1;
                this.preloadImages();
            } else {
                throw new Error(data.error || 'Failed to load achievements');
            }
        } catch (error) {
            console.error('Load achievements error:', error);
            throw error;
        }
    }

    preloadImages() {
        this.achievements.forEach(achievement => {
            if (!this.imageCache.has(achievement.imageUrl)) {
                const img = new Image();
                img.src = achievement.imageUrl;
                this.imageCache.set(achievement.imageUrl, img);
            }
        });
    }

    render() {
        if (this.achievements.length === 0) {
            this.renderEmpty();
            return;
        }

        // Filter out achievements with missing required data
        const validAchievements = this.achievements.filter(a => a && a.imageUrl && a.title);
        
        if (validAchievements.length === 0) {
            this.renderEmpty();
            return;
        }

        // Show the section since we have valid achievements
        const achievementsSection = document.getElementById('achievements');
        if (achievementsSection) {
            achievementsSection.style.display = '';
        }
        
        // Show navigation
        const galleryNav = document.querySelector('.gallery-nav');
        if (galleryNav) {
            galleryNav.style.display = '';
        }
        
        const swipeHint = document.querySelector('.swipe-hint');
        if (swipeHint) {
            swipeHint.style.display = '';
        }

        this.track.innerHTML = validAchievements.map((achievement, index) => `
            <div class="knight-card" 
                 onclick="window.achievementLightbox.open(${index})"
                 data-tooltip="${this.escapeHtml(achievement.hoverText || '')}">
                <div class="card-frame">
                    <div class="corner-piece top-left">♞</div>
                    <div class="corner-piece top-right">♞</div>
                    <img src="${achievement.imageUrl}" 
                         alt="${this.escapeHtml(achievement.title)}"
                         loading="lazy"
                         onerror="this.src='/images/banner.jpg'">
                    <div class="corner-piece bottom-left">♞</div>
                    <div class="corner-piece bottom-right">♞</div>
                </div>
                <div class="card-shine"></div>
                <div class="card-tooltip">${this.escapeHtml(achievement.hoverText || achievement.title)}</div>
            </div>
        `).join('');

        // Update achievements array to only valid ones for lightbox
        this.achievements = validAchievements;

        this.renderPagination();
        this.setupTooltips();
    }

    renderEmpty() {
        // Hide the entire achievements section when no achievements exist
        const achievementsSection = document.getElementById('achievements');
        if (achievementsSection) {
            achievementsSection.style.display = 'none';
        }
        
        // Clear the track and dots
        if (this.track) {
            this.track.innerHTML = '';
        }
        if (this.dotsContainer) {
            this.dotsContainer.innerHTML = '';
        }
        
        // Also hide navigation
        const galleryNav = document.querySelector('.gallery-nav');
        if (galleryNav) {
            galleryNav.style.display = 'none';
        }
        
        const swipeHint = document.querySelector('.swipe-hint');
        if (swipeHint) {
            swipeHint.style.display = 'none';
        }
    }

    renderError() {
        // On error, also hide the section to avoid showing broken UI
        this.renderEmpty();
    }

    renderPagination() {
        if (!this.dotsContainer || this.totalPages <= 1) {
            if (this.dotsContainer) {
                this.dotsContainer.innerHTML = '';
            }
            return;
        }

        this.dotsContainer.innerHTML = Array.from({ length: this.totalPages }, (_, i) => {
            const pageNum = i + 1;
            return `<span class="gallery-dot ${pageNum === this.currentPage ? 'active' : ''}" 
                          data-page="${pageNum}"></span>`;
        }).join('');

        // Add click handlers to dots
        this.dotsContainer.querySelectorAll('.gallery-dot').forEach(dot => {
            dot.addEventListener('click', async () => {
                const page = parseInt(dot.dataset.page);
                await this.goToPage(page);
            });
        });
    }

    async goToPage(page) {
        if (page < 1 || page > this.totalPages || page === this.currentPage) {
            return;
        }

        try {
            this.showLoading();
            await this.loadAchievements(page);
            this.render();
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('Failed to load page:', error);
            this.renderError();
        }
    }

    showLoading() {
        this.track.innerHTML = `
            <div class="loading-achievements">
                <div class="loading-spinner"></div>
                <p>Loading achievements...</p>
            </div>
        `;
    }

    setupNavigation() {
        // Scroll navigation
        const prevBtn = document.querySelector('.gallery-nav .prev');
        const nextBtn = document.querySelector('.gallery-nav .next');

        if (prevBtn) {
            prevBtn.addEventListener('click', () => this.scrollGallery(-1));
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => this.scrollGallery(1));
        }

        // Touch/swipe support
        let startX = 0;
        let scrollLeft = 0;

        this.track.addEventListener('touchstart', (e) => {
            startX = e.touches[0].pageX - this.track.offsetLeft;
            scrollLeft = this.track.scrollLeft;
        });

        this.track.addEventListener('touchmove', (e) => {
            const x = e.touches[0].pageX - this.track.offsetLeft;
            const walk = (x - startX) * 2;
            this.track.scrollLeft = scrollLeft - walk;
        });
    }

    scrollGallery(direction) {
        const cardWidth = 280 + 25; // card width + gap
        this.track.scrollBy({
            left: direction * cardWidth * 2,
            behavior: 'smooth'
        });
    }

    setupTooltips() {
        const cards = this.track.querySelectorAll('.knight-card');
        
        cards.forEach(card => {
            card.addEventListener('mouseenter', (e) => {
                const tooltip = card.querySelector('.card-tooltip');
                if (tooltip) {
                    tooltip.style.opacity = '1';
                    tooltip.style.visibility = 'visible';
                }
            });

            card.addEventListener('mouseleave', (e) => {
                const tooltip = card.querySelector('.card-tooltip');
                if (tooltip) {
                    tooltip.style.opacity = '0';
                    tooltip.style.visibility = 'hidden';
                }
            });
        });
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    getAchievements() {
        return this.achievements;
    }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
    window.achievementGallery = new AchievementGallery();
    window.achievementGallery.init();
});

export { AchievementGallery };
